import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Box, Button, FormControl, FormLabel, MenuItem, Select, Stack, TextField, FormHelperText } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { updateCompanyBilling, updateCompanyBillingReset } from "../../state/companyList/companyListSlice";
import { selectCompanyListState } from "../../state/companyList/companyListSelector";
import { BILLING_STATUS, BILLING_STATUS_LIST, BILLING_TYPE_LIST } from "../../helpers/constant/coreConstant";

const ManageBillingModal = ({ onClose, companyInfo }) => {
  const dispatch = useDispatch();
  const { isUpdating, isUpdateSuccess } = useSelector(selectCompanyListState);

  const defaultBillingStatus = companyInfo?.billingStatus || BILLING_STATUS.FREE;
  const defaultBillingType = companyInfo?.billingType || "MONTHLY";
  const defaultNextBillingDate = companyInfo?.nextBillingDate || "";
  const defaultBillingAmount = companyInfo?.billingAmount !== undefined && companyInfo?.billingAmount !== null ? companyInfo.billingAmount : "0.00";

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      billingStatus: defaultBillingStatus,
      billingType: defaultBillingType,
      nextBillingDate: defaultNextBillingDate,
      billingAmount: defaultBillingAmount,
    },
  });

  const watchBillingStatus = watch("billingStatus", defaultBillingStatus);

  useEffect(() => {
    if (isUpdateSuccess) {
      onClose();
      dispatch(updateCompanyBillingReset());
    }
  }, [isUpdateSuccess, onClose, dispatch]);

  const onSubmit = (data) => {
    if (companyInfo?.id) {
      const payload = {
        companyId: companyInfo.id,
        billingStatus: data.billingStatus,
        billingType: data.billingStatus === BILLING_STATUS.PAID ? data.billingType : "MONTHLY",
        nextBillingDate: data.billingStatus === BILLING_STATUS.PAID ? data.nextBillingDate : null,
        billingAmount: data.billingStatus === BILLING_STATUS.PAID ? parseFloat(data.billingAmount || 0) : 0,
      };

      dispatch(updateCompanyBilling(payload));
    }
  };

  return (
    <Box component="form" noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)} sx={{ pt: 1 }}>
      <Stack spacing={3}>
        {/* Billing Status */}
        <FormControl fullWidth required error={!!errors.billingStatus}>
          <FormLabel sx={{ mb: 1, fontWeight: 500, color: "text.primary" }}>Billing Status</FormLabel>
          <Select
            size="small"
            value={watchBillingStatus}
            onChange={(e) => setValue("billingStatus", e.target.value, { shouldValidate: true })}
          >
            {BILLING_STATUS_LIST.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </Select>
          {errors.billingStatus && <FormHelperText>{errors.billingStatus.message}</FormHelperText>}
        </FormControl>

        {/* Dynamic Paid Fields */}
        {watchBillingStatus === BILLING_STATUS.PAID && (
          <>
            {/* Billing Type */}
            <FormControl fullWidth required error={!!errors.billingType}>
              <FormLabel sx={{ mb: 1, fontWeight: 500, color: "text.primary" }}>Billing Type</FormLabel>
              <Select
                size="small"
                defaultValue={defaultBillingType}
                {...register("billingType", { required: "Billing Type is required" })}
              >
                {BILLING_TYPE_LIST.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.billingType && <FormHelperText>{errors.billingType.message}</FormHelperText>}
            </FormControl>

            {/* Next Billing Date */}
            <FormControl fullWidth required error={!!errors.nextBillingDate}>
              <FormLabel sx={{ mb: 1, fontWeight: 500, color: "text.primary" }}>Next Billing Date</FormLabel>
              <TextField
                fullWidth
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
                {...register("nextBillingDate", { required: "Next Billing Date is required" })}
                error={!!errors.nextBillingDate}
                helperText={errors.nextBillingDate?.message}
              />
            </FormControl>

            {/* Billing Amount */}
            <FormControl fullWidth required error={!!errors.billingAmount}>
              <FormLabel sx={{ mb: 1, fontWeight: 500, color: "text.primary" }}>Billing Amount ($)</FormLabel>
              <TextField
                fullWidth
                size="small"
                type="number"
                inputProps={{ step: "0.01", min: "0" }}
                placeholder="0.00"
                {...register("billingAmount", {
                  required: "Billing Amount is required",
                  min: { value: 0, message: "Amount must be greater than or equal to 0" },
                })}
                error={!!errors.billingAmount}
                helperText={errors.billingAmount?.message}
              />
            </FormControl>
          </>
        )}

        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button variant="outlined" color="inherit" onClick={onClose} disabled={isUpdating}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary" disabled={isUpdating}>
            {isUpdating ? "Saving..." : "Save Changes"}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default ManageBillingModal;
